// ===================================================================
// Time & Labor — Code Configuration (Admin Settings)
// Manage account codes, program codes, and shift premium multipliers
// ===================================================================

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, Table, Button, Group, TextInput, NumberInput,
  Stack, ActionIcon, LoadingOverlay,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconTrash, IconDeviceFloppy, IconSettings,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useModulePermission } from '../../hooks/useModulePermission';
import { WORKER_URL } from '../../lib/api-switch';

const TEAL = '#0D9488';
const H = { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' };

interface CodeEntry { code: string; description: string; }
interface ShiftPremiumEntry { type: string; label: string; multiplier: number; description: string; }

const DEFAULT_PREMIUMS: ShiftPremiumEntry[] = [
  { type: 'none', label: 'None', multiplier: 1.0, description: 'Standard rate — no premium applied' },
  { type: 'night', label: 'Night Differential', multiplier: 1.1, description: '10% premium for night shifts' },
  { type: 'weekend', label: 'Weekend', multiplier: 1.5, description: '50% premium for weekend work' },
  { type: 'holiday', label: 'Holiday', multiplier: 2.0, description: '100% premium for holiday work' },
  { type: 'hazard', label: 'Hazard Pay', multiplier: 1.25, description: '25% premium for hazardous conditions' },
];

async function loadKV<T>(key: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(`${WORKER_URL}/api/config/${key}`, { headers: H });
    if (!res.ok) return fallback;
    const data = await res.json();
    return (Array.isArray(data) || typeof data === 'object') ? data as T : fallback;
  } catch { return fallback; }
}

async function saveKV(key: string, data: unknown): Promise<boolean> {
  try {
    const res = await fetch(`${WORKER_URL}/api/config/${key}`, {
      method: 'PUT', headers: H, body: JSON.stringify(data),
    });
    return res.ok;
  } catch { return false; }
}

export default function CodeConfig() {
  const { canAdmin } = useModulePermission('timelabor');

  const [loading, setLoading] = useState(true);
  const [accountCodes, setAccountCodes] = useState<CodeEntry[]>([]);
  const [programCodes, setProgramCodes] = useState<CodeEntry[]>([]);
  const [shiftPremiums, setShiftPremiums] = useState<ShiftPremiumEntry[]>(DEFAULT_PREMIUMS);

  const [savingAccounts, setSavingAccounts] = useState(false);
  const [savingPrograms, setSavingPrograms] = useState(false);
  const [savingPremiums, setSavingPremiums] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [ac, pc, sp] = await Promise.all([
        loadKV<CodeEntry[]>('timelabor-account-codes', []),
        loadKV<CodeEntry[]>('timelabor-program-codes', []),
        loadKV<ShiftPremiumEntry[]>('timelabor-shift-premiums', []),
      ]);
      setAccountCodes(ac);
      setProgramCodes(pc);
      // Merge saved premiums with defaults (ensure "none" always present)
      if (sp.length > 0) {
        const merged = DEFAULT_PREMIUMS.map(d => {
          const saved = sp.find((s: ShiftPremiumEntry) => s.type === d.type);
          return saved ? { ...d, multiplier: saved.multiplier, description: saved.description } : d;
        });
        setShiftPremiums(merged);
      }
      setLoading(false);
    })();
  }, []);

  // ── Account Codes ──────────────────────────────────────────────
  const addAccountCode = () => setAccountCodes(prev => [...prev, { code: '', description: '' }]);
  const removeAccountCode = (idx: number) => setAccountCodes(prev => prev.filter((_, i) => i !== idx));
  const updateAccountCode = (idx: number, field: keyof CodeEntry, val: string) =>
    setAccountCodes(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));

  const saveAccountCodes = async () => {
    const valid = accountCodes.filter(c => c.code.trim());
    const codes = valid.map(c => c.code.trim().toLowerCase());
    const dupes = codes.filter((c, i) => codes.indexOf(c) !== i);
    if (dupes.length > 0) {
      notifications.show({ title: 'Duplicate Code', message: `Duplicate account code(s): ${[...new Set(dupes)].join(', ')}`, color: 'red' });
      return;
    }
    setSavingAccounts(true);
    const ok = await saveKV('timelabor-account-codes', valid);
    if (ok) {
      setAccountCodes(valid);
      notifications.show({ title: 'Saved', message: `${valid.length} account code(s) saved`, color: TEAL });
    } else {
      notifications.show({ title: 'Error', message: 'Failed to save account codes', color: 'red' });
    }
    setSavingAccounts(false);
  };

  // ── Program Codes ──────────────────────────────────────────────
  const addProgramCode = () => setProgramCodes(prev => [...prev, { code: '', description: '' }]);
  const removeProgramCode = (idx: number) => setProgramCodes(prev => prev.filter((_, i) => i !== idx));
  const updateProgramCode = (idx: number, field: keyof CodeEntry, val: string) =>
    setProgramCodes(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));

  const saveProgramCodes = async () => {
    const valid = programCodes.filter(c => c.code.trim());
    const codes = valid.map(c => c.code.trim().toLowerCase());
    const dupes = codes.filter((c, i) => codes.indexOf(c) !== i);
    if (dupes.length > 0) {
      notifications.show({ title: 'Duplicate Code', message: `Duplicate program code(s): ${[...new Set(dupes)].join(', ')}`, color: 'red' });
      return;
    }
    setSavingPrograms(true);
    const ok = await saveKV('timelabor-program-codes', valid);
    if (ok) {
      setProgramCodes(valid);
      notifications.show({ title: 'Saved', message: `${valid.length} program code(s) saved`, color: TEAL });
    } else {
      notifications.show({ title: 'Error', message: 'Failed to save program codes', color: 'red' });
    }
    setSavingPrograms(false);
  };

  // ── Shift Premiums ─────────────────────────────────────────────
  const updatePremium = (idx: number, field: 'multiplier' | 'description', val: number | string) =>
    setShiftPremiums(prev => prev.map((p, i) => i === idx ? { ...p, [field]: val } : p));

  const savePremiums = async () => {
    setSavingPremiums(true);
    const ok = await saveKV('timelabor-shift-premiums', shiftPremiums);
    if (ok) {
      notifications.show({ title: 'Saved', message: 'Shift premium configuration saved', color: TEAL });
    } else {
      notifications.show({ title: 'Error', message: 'Failed to save shift premiums', color: 'red' });
    }
    setSavingPremiums(false);
  };

  if (!canAdmin) {
    return (
      <Stack gap="md">
        <Title order={3}>Settings</Title>
        <Paper p="xl" withBorder ta="center">
          <IconSettings size={48} color="gray" />
          <Text c="dimmed" mt="md">Admin access required to manage Time & Labor settings.</Text>
        </Paper>
      </Stack>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading && accountCodes.length === 0} />

      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>Settings</Title>
          <Text size="sm" c="dimmed">Configure account codes, program codes, and shift premium multipliers</Text>
        </div>
      </Group>

      <Stack gap="xl">
        {/* ═══════════════════════════════════════════════════════════ */}
        {/* ACCOUNT CODES                                               */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <Paper p="md" withBorder style={{ borderTop: `3px solid ${TEAL}` }}>
          <Group justify="space-between" mb="md">
            <div>
              <Text fw={600} size="lg">Account Codes</Text>
              <Text size="xs" c="dimmed">Organizational budget codes assigned to time entries for financial tracking</Text>
            </div>
            <Group gap={6}>
              <Button variant="light" color={TEAL} size="xs" leftSection={<IconPlus size={14} />} onClick={addAccountCode}>
                Add Code
              </Button>
              <Button color={TEAL} size="xs" leftSection={<IconDeviceFloppy size={14} />}
                onClick={saveAccountCodes} loading={savingAccounts}>
                Save
              </Button>
            </Group>
          </Group>

          {accountCodes.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No account codes configured. Click "Add Code" to create your first one.
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 200 }}>Code</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th style={{ width: 40 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {accountCodes.map((c, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <TextInput size="xs" placeholder="e.g. 001-4100" value={c.code}
                        onChange={e => updateAccountCode(i, 'code', e.target.value)} />
                    </Table.Td>
                    <Table.Td>
                      <TextInput size="xs" placeholder="e.g. General Fund — Public Works" value={c.description}
                        onChange={e => updateAccountCode(i, 'description', e.target.value)} />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon size="xs" variant="subtle" color="red" onClick={() => removeAccountCode(i)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* PROGRAM CODES                                               */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <Paper p="md" withBorder style={{ borderTop: `3px solid ${TEAL}` }}>
          <Group justify="space-between" mb="md">
            <div>
              <Text fw={600} size="lg">Program Codes</Text>
              <Text size="xs" c="dimmed">Grant or program identifiers for categorizing labor costs by funding source</Text>
            </div>
            <Group gap={6}>
              <Button variant="light" color={TEAL} size="xs" leftSection={<IconPlus size={14} />} onClick={addProgramCode}>
                Add Code
              </Button>
              <Button color={TEAL} size="xs" leftSection={<IconDeviceFloppy size={14} />}
                onClick={saveProgramCodes} loading={savingPrograms}>
                Save
              </Button>
            </Group>
          </Group>

          {programCodes.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No program codes configured. Click "Add Code" to create your first one.
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 200 }}>Code</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th style={{ width: 40 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {programCodes.map((c, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <TextInput size="xs" placeholder="e.g. DR-4815-GA" value={c.code}
                        onChange={e => updateProgramCode(i, 'code', e.target.value)} />
                    </Table.Td>
                    <Table.Td>
                      <TextInput size="xs" placeholder="e.g. FEMA PA — Disaster 4815" value={c.description}
                        onChange={e => updateProgramCode(i, 'description', e.target.value)} />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon size="xs" variant="subtle" color="red" onClick={() => removeProgramCode(i)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* SHIFT PREMIUMS                                              */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <Paper p="md" withBorder style={{ borderTop: `3px solid ${TEAL}` }}>
          <Group justify="space-between" mb="md">
            <div>
              <Text fw={600} size="lg">Shift Premium Configuration</Text>
              <Text size="xs" c="dimmed">Pay multipliers applied to base rates for non-standard shifts</Text>
            </div>
            <Button color={TEAL} size="xs" leftSection={<IconDeviceFloppy size={14} />}
              onClick={savePremiums} loading={savingPremiums}>
              Save
            </Button>
          </Group>

          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 180 }}>Premium Type</Table.Th>
                <Table.Th style={{ width: 140 }}>Multiplier</Table.Th>
                <Table.Th>Description</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {shiftPremiums.map((p, i) => (
                <Table.Tr key={p.type}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{p.label}</Text>
                  </Table.Td>
                  <Table.Td>
                    {p.type === 'none' ? (
                      <Text size="sm" c="dimmed">1.0x (fixed)</Text>
                    ) : (
                      <NumberInput size="xs" min={1.0} max={5.0} step={0.05} decimalScale={2}
                        value={p.multiplier}
                        onChange={v => updatePremium(i, 'multiplier', typeof v === 'number' ? v : 1.0)}
                        suffix="x"
                        styles={{ input: { width: 100 } }} />
                    )}
                  </Table.Td>
                  <Table.Td>
                    <TextInput size="xs" value={p.description}
                      onChange={e => updatePremium(i, 'description', e.target.value)}
                      disabled={p.type === 'none'} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      </Stack>
    </div>
  );
}
